import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface FileUploadProps {
  currentImageUrl?: string | null;
  helperText?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  showLabel?: boolean;
  onFileChange: (file: File | null) => void;
}

export function FileUpload({
  currentImageUrl,
  helperText,
  disabled,
  error,
  label = 'Profile image',
  showLabel = true,
  onFileChange,
}: FileUploadProps): JSX.Element {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentImageUrl ?? null,
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [hasCustomFile, setHasCustomFile] = useState(false);

  useEffect(() => {
    if (!hasCustomFile) {
      setPreviewUrl(currentImageUrl ?? null);
    }
  }, [currentImageUrl, hasCustomFile]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const clearBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const handleFiles = useCallback(
    (file: File | null) => {
      if (!file) {
        clearBlobUrl();
        setHasCustomFile(false);
        setValidationError(null);
        setPreviewUrl(currentImageUrl ?? null);
        onFileChange(null);
        return;
      }

      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        setValidationError('Only JPEG, PNG, or WEBP files are allowed.');
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setValidationError('Files must be smaller than 5 MB.');
        return;
      }

      setValidationError(null);
      clearBlobUrl();
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
      setHasCustomFile(true);
      onFileChange(file);
    },
    [clearBlobUrl, currentImageUrl, onFileChange],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      handleFiles(file);
    },
    [handleFiles],
  );

  const handleClear = useCallback(() => {
    handleFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFiles]);

  const triggerInput = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const helperNode = useMemo(
    () => helperText && <p className="text-xs text-muted-foreground">{helperText}</p>,
    [helperText],
  );

  const shouldRenderLabel = showLabel && label;

  return (
    <div className="space-y-2">
      {shouldRenderLabel && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{label}</Label>
          {helperNode}
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={triggerInput}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerInput();
          }
        }}
        className={cn(
          'flex items-center gap-4 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 transition hover:border-primary hover:bg-primary/10',
          disabled && 'cursor-not-allowed opacity-70',
        )}
      >
        <Avatar className="h-14 w-14">
          {previewUrl ? (
            <AvatarImage src={previewUrl} alt="Tenant profile" />
          ) : (
            <AvatarFallback>
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Logo
              </span>
            </AvatarFallback>
          )}
        </Avatar>
        <div>
          <p className="text-sm font-medium text-foreground">
            {previewUrl ? 'Change image' : 'Upload logo or avatar'}
          </p>
          <p className="text-xs text-muted-foreground">
            JPEG, PNG or WEBP · max 5 MB
          </p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME_TYPES.join(',')}
        className="sr-only"
        disabled={disabled}
        onChange={handleInputChange}
      />
      <div className="flex gap-2">
        {previewUrl && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={disabled}
          >
            Remove image
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={triggerInput}
          disabled={disabled}
        >
          {previewUrl ? 'Replace' : 'Select file'}
        </Button>
      </div>
      {(validationError || error) && (
        <p className="text-xs text-destructive">
          {validationError || error}
        </p>
      )}
    </div>
  );
}
